// Renovação ANTECIPADA do access token.
//
// Sem isto, o refresh só acontecia reativamente: o token expirava, a próxima
// requisição levava 401 e só então a sessão era renovada. Com TTL de 15 min e várias
// queries em polling (5s, 15s, 30s), cada expiração virava uma rajada de 401
// simultâneos — e bastava uma delas cair junto de um soluço do backend para o app
// concluir "sessão inválida" e deslogar o usuário.
//
// Renovando ~1 min antes do `exp`, o 401 deixa de fazer parte da operação normal e
// passa a ser o que deveria ser: sinal de que algo está errado de verdade.
import { getToken, readTokenExp } from './token-storage';
import { refreshAccessToken } from './refresh';

/// Folga antes do `exp`. Cobre relógio dessincronizado e latência da chamada.
const RENEW_SKEW_MS = 60_000;
/// Piso do agendamento: evita loop apertado se o token já estiver quase vencido.
const MIN_DELAY_MS = 1_000;
/// Espera após uma falha transitória, para não martelar um backend que já está mal.
const RETRY_DELAY_MS = 30_000;

let timer: ReturnType<typeof setTimeout> | null = null;
let started = false;

function clearTimer(): void {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
}

/**
 * (Re)agenda a renovação a partir do `exp` do token atual. Sem token, não agenda —
 * quem cuida do cold start é o AuthBootstrap.
 */
export function scheduleTokenRefresh(): void {
  clearTimer();
  const token = getToken();
  if (!token) return;

  const exp = readTokenExp(token);
  // Token sem `exp` legível: não dá para antecipar nada. O 401 do baseQuery continua
  // sendo a rede de segurança.
  if (exp === null) return;

  const delay = Math.max(MIN_DELAY_MS, exp * 1000 - RENEW_SKEW_MS - Date.now());
  timer = setTimeout(() => {
    void renewNow();
  }, delay);
}

async function renewNow(): Promise<void> {
  const result = await refreshAccessToken();
  if (result.ok) {
    scheduleTokenRefresh();
    return;
  }
  // Recusa definitiva: não adianta insistir — o 401 da próxima requisição encerra a
  // sessão pelo caminho normal do baseQuery, que é quem sabe avisar o Redux.
  if (result.reason === 'auth') return;
  // Transitório: tenta de novo mais tarde. A sessão continua de pé.
  clearTimer();
  timer = setTimeout(() => {
    void renewNow();
  }, RETRY_DELAY_MS);
}

/**
 * Liga o agendador. Idempotente.
 *
 * O listener de `visibilitychange` existe porque timers de aba em segundo plano são
 * estrangulados pelo browser (e não correm com o notebook suspenso): ao voltar, o
 * prazo pode já ter passado. Reagendar no retorno garante a renovação antes que o
 * usuário clique em qualquer coisa.
 */
export function startTokenRefreshScheduler(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') scheduleTokenRefresh();
  });
  scheduleTokenRefresh();
}

/** Desliga o agendamento (logout / sessão encerrada). */
export function stopTokenRefreshScheduler(): void {
  clearTimer();
}
